import React, {  Fragment, useState } from 'react';
import { FormikHandlers, FormikErrors, FormikTouched } from 'formik';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { distances } from '../../../../styles/constants';
import { MarketingInput, DiscountSection, ImageUploader, EditWrapper } from '../../../common/components';
import ItemsSelection from '../../containers/ItemsSelection';
import { Checkbox, Radiobutton } from '../../../../components/Forms';
import { Campaign, DiscountItem, DiscountType, GroupItems, AssetRequestObject } from '../../../types';
import { validateNested } from '../../helpers';
import { useAccount } from '../../../../settings/account/hooks';
import { getDefaultCountryCurrency } from '../../../../helpers/currency';

interface PromotionProps {
    discount: DiscountItem;
    index: number;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleChange: FormikHandlers['handleChange'];
    handleBlur: FormikHandlers['handleBlur'];
    errors: FormikErrors<Campaign>;
    touched: FormikTouched<Campaign>;
    createAsset: (asset: AssetRequestObject) => void;
    removeAsset: (discountId: string) => void;
    isEditable?: boolean;
    isScheduledOrActive?: boolean;
    scopes: string[];
    asset?: string;
    isMccOnly?: boolean;
}

const Promotion = (
    {
        discount,
        index,
        setFieldValue,
        handleChange,
        handleBlur,
        errors,
        touched,
        createAsset,
        removeAsset,
        isEditable,
        isScheduledOrActive,
        asset,
        isMccOnly,
    }: PromotionProps
) => {
    const { t } = useTranslation();
    const { account } = useAccount();
    const validate = validateNested(index, errors, touched);
    const hasUnlimitedUsage = (
        discount.discountUsageAmount === '-1' ||
        discount.discountUsageAmount === '' ||
        discount.discountUsageAmount === undefined
    );
    const [maxUsage, setMaxUsage] = useState(!hasUnlimitedUsage);
    const [discountCombination, setDiscountCombination] = useState(discount.discountCombination === undefined || discount.discountCombination  === -1);

    const getDiscountValueLabel = (type: DiscountType) => {
        if (type === DiscountType.discountInAmount) {
            return t('marketing.campaign.discount_currency', { currency: getDefaultCountryCurrency(account, ['NOK', 'SEK'], 'NOK') });
        } else if (type === DiscountType.discountInPercentage) {
            return t('marketing.campaign.discount_percentage');
        } else if (type === DiscountType.fixedPriceDiscount) {
            return t('marketing.campaign.new_price');
        } else if (type === DiscountType.amountBuyDiscount) {
            return t('marketing.campaign.buy');
        }
        return '';
    };

    return (
        <Fragment>
            <EditWrapper isEditable={!!isEditable}>
                <DiscountSection label={t('marketing.campaign.discount')}>
                    <DiscountValuesWrapper>
                        <MarketingInput
                            type="number"
                            label={getDiscountValueLabel(discount.type)}
                            labelOpacity={1}
                            placeholder=""
                            name={`discounts.${index}.discountValue`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={discount.discountValue}
                            validation={validate('discountValue')}
                        />
                        {/* {discount.type === DiscountType.discountInNok && (
                            <MarketingInput
                                type='number'
                                label={t('marketing.campaign.total_purchase')}
                                labelOpacity={1}
                                placeholder=''
                                name={`discounts.${index}.totalPurchase`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.totalPurchase}
                                validation={validate('totalPurchase')}
                            />
                        )} */}
                        {discount.type === DiscountType.amountBuyDiscount && (
                            <MarketingInput
                                type="number"
                                label={t('marketing.campaign.pay_for')}
                                labelOpacity={1}
                                placeholder=""
                                name={`discounts.${index}.payFor`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.payFor}
                                validation={validate('payFor')}
                            />
                        )}
                    </DiscountValuesWrapper>
                    <DiscountValuesWrapper>
                        <div>
                            <div>
                                <Checkbox name={`discounts.${index}.discount_max_usage_toggle`}
                                    label={t('marketing.campaign.discount_max_usage_toggle')}
                                    checked={maxUsage}
                                    onChange={async () => {
                                        if (!maxUsage) {
                                            await setFieldValue(`discounts.${index}.discountUsageAmount`, '1');
                                        } else {
                                            await setFieldValue(`discounts.${index}.discountUsageAmount`, '-1');
                                        }
                                        setMaxUsage(!maxUsage);
                                    }} />
                            </div>
                            {maxUsage && <MarketingInput
                                type="number"
                                label={t('marketing.campaign.discount_max_usage')}
                                labelOpacity={1}
                                placeholder=""
                                min={1}
                                name={`discounts.${index}.discountUsageAmount`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.discountUsageAmount}
                                validation={validate('discountUsageAmount')}

                            />}
                            <br/>
                            <div>
                                {
                                    <Checkbox name={`discounts.${index}.discount_combination`}
                                        label={t('marketing.campaign.discount_combination')}
                                        checked={discountCombination}
                                        onChange={async () => {
                                            if (!discountCombination) {
                                                await setFieldValue(`discounts.${index}.discountCombination`, -1);
                                            } else {
                                                await setFieldValue(`discounts.${index}.discountCombination`, 0);
                                            }
                                            setDiscountCombination(!discountCombination);
                                        }} />
                                }
                            </div>
                        </div>
                    </DiscountValuesWrapper>
                </DiscountSection>
                <DiscountSection label={t('marketing.campaign.choose_items')}>
                    <GroupItemsWrapper>
                        <Radiobutton
                            name={`discounts.${index}.groupItems`}
                            label={t('marketing.campaign.all_items')}
                            value={discount.groupItems === GroupItems.allItems}
                            onChange={() => setFieldValue(`discounts.${index}.groupItems`, GroupItems.allItems)}
                        />
                        {!isMccOnly && (
                            <Radiobutton
                                name={`discounts.${index}.groupItems`}
                                label={t('marketing.campaign.group_items')}
                                value={discount.groupItems === GroupItems.itemsGrouped}
                                onChange={() => setFieldValue(`discounts.${index}.groupItems`, GroupItems.itemsGrouped)}
                            />
                        )}
                    </GroupItemsWrapper>
                    {discount.groupItems === GroupItems.itemsGrouped && (
                        <ItemsSelection
                            name={`discounts.${index}.items`}
                            items={discount.items}
                            handleBlur={handleBlur}
                        />
                    )}
                </DiscountSection>
            </EditWrapper>
            <EditWrapper isEditable={!!isScheduledOrActive}>
                <DiscountSection label={t('marketing.campaign.discount_details')}>
                    <DiscountDetailsWrapper>
                        <DetailsInputWrapper>
                            <MarketingInput
                                type="text"
                                label={t('marketing.campaign.title')}
                                labelOpacity={1}
                                placeholder=""
                                name={`discounts.${index}.title`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.title}
                                validation={validate('title')}
                            />
                            <MarketingInput
                                type="textarea"
                                label={t('marketing.campaign.description')}
                                labelOpacity={1}
                                placeholder=""
                                name={`discounts.${index}.description`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.description}
                                isTextArea
                                validation={validate('description')}
                            />
                            <MarketingInput
                                type="textarea"
                                label={t('marketing.campaign.metadata_dintero_exclude_public_description')}
                                labelOpacity={1}
                                placeholder={t('marketing.campaign.metadata_dintero_exclude_public_description_placeholder')}
                                name={`discounts.${index}.metadata_dintero_exclude_public_description`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.metadata_dintero_exclude_public_description || ''}
                                validation={validate('metadata_dintero_exclude_public_description')}
                            />
                            {/* <Checkbox
                                name={`discounts.${index}.emailSendout`}
                                label={t('marketing.campaign.item_in_send_out')}
                                checked={discount.emailSendout}
                                onChange={() => setFieldValue(`discounts.${index}.emailSendout`, !discount.emailSendout)}
                            /> */}
                            <MarketingInput
                                type="textarea"
                                label={t('marketing.campaign.add_webshop_link')}
                                labelOpacity={1}
                                placeholder="https://example.com"
                                name={`discounts.${index}.webshop`}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={discount.webshop || ''}
                                validation={validate('webshop')}
                            />
                        </DetailsInputWrapper>
                        <UploaderWrapper>
                            <ImageUploader
                                createAsset={createAsset}
                                discountId={discount.id}
                                removeAsset={removeAsset}
                                asset={asset}
                            />
                        </UploaderWrapper>
                    </DiscountDetailsWrapper>
                </DiscountSection>
            </EditWrapper>
        </Fragment>
    );
};

const DiscountValuesWrapper = styled.div`
    display: flex;
    margin-bottom: ${distances.small};

    > *:first-child {
        margin-right: ${distances.small};
    }

    @media (max-width: 768px) {
        display: block;
        width: 100%;

        > *:first-child {
            margin-right: 0;
        }
    }
`;

const DiscountDetailsWrapper = styled.div`
    display: flex;

    @media (max-width: 768px) {
        display: block;
        width: 100%;
    }
`;

const DetailsInputWrapper = styled.div`
    width: calc(50% - ${distances.tiny});
    margin-right: ${distances.tiny};

    @media (max-width: 768px) {
        width: 100%;
        margin-right: 0;
    }
`;

const UploaderWrapper = styled.div`
    height: 100%;
    width: calc(50% - ${distances.tiny});
    margin-left: ${distances.tiny};

    @media (max-width: 768px) {
        width: 100%;
        margin-left: 0;
    }
`;

const GroupItemsWrapper = styled.div`
    display: flex;
    width: 35%;
    justify-content: space-between;
    margin: ${distances.normal} 0;

    > *:first-child {
        margin-right: ${distances.small};
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

export default Promotion;
